import { getToken, onMessage } from 'firebase/messaging';
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  Timestamp
} from 'firebase/firestore';
import { messaging, db } from './firebase';
import { createNotification, NOTIFICATION_TYPES } from './notificationService';
import { pushToast } from '../providers/NotificationToastProvider';

// VAPID key - Generate from Firebase Console > Project Settings > Cloud Messaging > Web Push certificates
// Replace 'YOUR_VAPID_KEY_HERE' with your actual VAPID key
const VAPID_KEY = 'BHzyU4P4uhPmBPuXilYiZofRDdHPwvH6wZd13TW4MZCJo680wvJjHPr-D3hoqW93gWfQfUT1QOyZyTdN6kXBK6U';

/**
 * Request notification permission and get FCM token
 * @param {string} userId - User ID
 * @returns {Object} Result with token
 */
export const requestNotificationPermission = async (userId) => {
  try {
    if (!messaging) {
      console.warn('Firebase Messaging not supported');
      return { success: false, error: 'Messaging not supported' };
    }

    // Request permission
    const permission = await Notification.requestPermission();

    if (permission !== 'granted') {
      console.log('Notification permission denied');
      return { success: false, error: 'Permission denied' };
    }

    // Get FCM token
    const token = await getToken(messaging, { vapidKey: VAPID_KEY });

    if (token) {
      console.log('FCM Token:', token);

      // Save token to Firestore
      await saveFCMToken(userId, token);

      return { success: true, token };
    } else {
      console.log('No registration token available');
      return { success: false, error: 'No token available' };
    }
  } catch (error) {
    console.error('Error getting FCM token:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Ensure the current user has an FCM token saved
 * Only requests permission / generates token if missing
 * @param {string} userId
 */
export const ensureUserHasFCMToken = async (userId) => {
  if (!userId) {
    return { success: false, error: 'User ID is required' };
  }

  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const existingToken = userDoc.exists() ? userDoc.data()?.fcmToken : null;

    if (existingToken) {
      return { success: true, token: existingToken, alreadyExists: true };
    }

    if (typeof window === 'undefined' || !('Notification' in window)) {
      return { success: false, error: 'Notifications are not supported in this environment' };
    }

    if (Notification.permission === 'denied') {
      console.warn('Notification permission was previously denied');
      return { success: false, error: 'Notification permission denied' };
    }

    // If permission is default or granted, request/generate the token
    return await requestNotificationPermission(userId);
  } catch (error) {
    console.error('Error ensuring FCM token:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Save FCM token to Firestore
 * @param {string} userId - User ID
 * @param {string} token - FCM token
 */
export const saveFCMToken = async (userId, token) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      await updateDoc(userRef, {
        fcmToken: token,
        fcmTokenUpdatedAt: Timestamp.now()
      });
    } else {
      await setDoc(userRef, {
        fcmToken: token,
        fcmTokenUpdatedAt: Timestamp.now()
      }, { merge: true });
    }

    console.log('FCM token saved successfully');
    return { success: true };
  } catch (error) {
    console.error('Error saving FCM token:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Subscribe user to notification topics
 * @param {string} userId - User ID
 * @param {Array} topics - Array of topics to subscribe
 */
export const subscribeToTopics = async (userId, topics) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      return { success: false, error: 'User not found' };
    }

    // Save subscribed topics to user document
    await updateDoc(userRef, {
      subscribedTopics: arrayUnion(...topics),
      topicsUpdatedAt: Timestamp.now()
    });

    console.log('Subscribed to topics:', topics);
    return { success: true, topics };
  } catch (error) {
    console.error('Error subscribing to topics:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Unsubscribe user from notification topics
 * @param {string} userId - User ID
 * @param {Array} topics - Array of topics to unsubscribe
 */
export const unsubscribeFromTopics = async (userId, topics) => {
  try {
    const userRef = doc(db, 'users', userId);

    await updateDoc(userRef, {
      subscribedTopics: arrayRemove(...topics),
      topicsUpdatedAt: Timestamp.now()
    });

    console.log('Unsubscribed from topics:', topics);
    return { success: true, topics };
  } catch (error) {
    console.error('Error unsubscribing from topics:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get user's subscribed topics
 * @param {string} userId - User ID
 * @returns {Object} Result with topics array
 */
export const getUserSubscribedTopics = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      return { success: false, error: 'User not found' };
    }

    const topics = userDoc.data().subscribedTopics || [];
    return { success: true, topics };
  } catch (error) {
    console.error('Error getting subscribed topics:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Setup foreground message listener
 * @param {Function} callback - Callback function to handle messages
 */
export const setupMessageListener = (callback) => {
  if (!messaging) {
    console.warn('Firebase Messaging not supported');
    return null;
  }

  return onMessage(messaging, (payload) => {
    console.log('Message received in foreground:', payload);

    // Show notification
    if (payload.notification) {
      const { title, body, icon } = payload.notification;

      if (Notification.permission === 'granted') {
        new Notification(title, {
          body,
          icon: icon || '/logo192.png',
          badge: '/logo192.png'
        });
      }

      // Trigger in-app toast
      pushToast({
        title,
        message: body,
        type: payload.data?.type || 'info'
      });
    }

    // Call callback with payload
    if (callback) {
      callback(payload);
    }
  });
};

/**
 * Create notification request for backend to send
 * This creates a document in Firestore that triggers a Cloud Function
 * @param {Object} notificationData - Notification data
 */
export const sendNotificationRequest = async (notificationData) => {
  try {
    const {
      type, // 'topic' or 'personal'
      topic, // For topic-based notifications
      userId, // For personal notifications
      title,
      body,
      data
    } = notificationData;

    const notificationDoc = {
      type,
      title,
      body,
      data: data || {},
      createdAt: Timestamp.now(),
      status: 'pending'
    };

    if (type === 'topic') {
      notificationDoc.topic = topic;
    } else if (type === 'personal') {
      notificationDoc.userId = userId;
    }

    // Save to Firestore - Cloud Function will process this
    const docRef = await setDoc(
      doc(db, 'notificationQueue', `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`),
      notificationDoc
    );

    console.log('Notification request created');
    return { success: true };
  } catch (error) {
    console.error('Error creating notification request:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Send notification to all users subscribed to specific topics
 * @param {Array} topics - Array of topics (e.g., ['industry_Technology', 'education_Bachelor'])
 * @param {Object} notification - Notification data
 */
export const sendTopicNotification = async (topics, notification) => {
  try {
    const { title, body, data } = notification;

    // Create notification requests for each topic (for FCM push)
    const promises = topics.map(topic =>
      sendNotificationRequest({
        type: 'topic',
        topic,
        title,
        body,
        data
      })
    );

    await Promise.all(promises);
    console.log('Topic notifications queued for:', topics);
    return { success: true, topics };
  } catch (error) {
    console.error('Error sending topic notifications:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Send personal notification to a specific user
 * @param {string} userId - User ID
 * @param {Object} notification - Notification data
 */
export const sendPersonalNotification = async (userId, notification) => {
  try {
    const { title, body, data } = notification;

    // Queue FCM push notification
    await sendNotificationRequest({
      type: 'personal',
      userId,
      title,
      body,
      data
    });

    // Also create in-app notification
    try {
      let notificationType = NOTIFICATION_TYPES.NEW_APPLICATION;
      if (data.type === 'new_application') {
        notificationType = NOTIFICATION_TYPES.NEW_APPLICATION;
      } else if (data.type === 'application_status_update') {
        notificationType = NOTIFICATION_TYPES.APPLICATION_STATUS_UPDATE;
      } else if (data.type === 'new_job') {
        notificationType = NOTIFICATION_TYPES.NEW_JOB;
      }

      await createNotification({
        userId,
        type: notificationType,
        title,
        message: body,
        data
      });
    } catch (inAppError) {
      console.error('Error creating in-app notification:', inAppError);
    }

    console.log('Personal notification sent to user:', userId);
    return { success: true, userId };
  } catch (error) {
    console.error('Error sending personal notification:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Generate topic names from job data
 * @param {Object} jobData - Job data
 * @returns {Array} Array of topic names
 */
export const generateTopicsFromJob = (jobData) => {
  const topics = [];

  // Add industry topic
  if (jobData.industry) {
    topics.push(`industry_${jobData.industry.replace(/\s+/g, '_')}`);
  }

  // Add education topic
  if (jobData.education) {
    topics.push(`education_${jobData.education.replace(/\s+/g, '_')}`);
  }

  return topics;
};

/**
 * Get available notification topics for job seekers
 * @returns {Object} Object with industry and education topics
 */
export const getAvailableTopics = () => {
  return {
    industries: [
      'Automobile / Auto Ancillary',
      'Engineering / Fabrication',
      'Foundry / Casting',
      'Plastic / Injection Moulding',
      'Textile / Garments',
      'Food Processing',
      'Packaging Industry',
      'Chemical Industry',
      'Pharma Manufacturing',
      'Electrical / Electronics Manufacturing',
      'Paper / Printing',
      'Steel / Metal Industry',
      'Security Services',
      'Housekeeping Services',
      'Facility Management',
      'Construction / Civil',
      'Maintenance & Repair',
      'Warehouse / Logistics',
      'Courier & Delivery',
      'Driver / Transport Services',
      'Manpower / Staffing Agency',
      'Pest Control Services',
      'Hotel / Hospitality',
      'Restaurant / Cafe / Food Service',
      'Travel & Tourism',
      'Event Management',
      'Salon / Spa / Beauty',
      'Fitness / Gym',
      'Laundry Services',
      'FMCG',
      'Supermarket / Hypermarket',
      'Showroom / Electronics Retail',
      'Jewellery Retail',
      'Mobile Shop / Electronics Shop',
      'Automobile Showroom',
      'BPO / Call Center',
      'KPO / Back Office',
      'E-commerce',
      'IT Services / Software',
      'Digital Marketing Agency',
      'Advertising / Marketing Agency',
      'HR / Recruitment Agency',
      'Hospital / Clinic',
      'Diagnostic Lab',
      'Medical Store / Pharmacy',
      'Medical Equipment',
      'Nursing / Care Services',
      'School',
      'College',
      'Coaching Classes',
      'EdTech / Online Education',
      'Agriculture / Agri Services',
      'Dairy / Poultry',
      'Cold Storage',
      'Seeds / Fertilizer',
      'Agro Processing',
      'Other'
    ],
    educationRequirements: [
      'Bachelor\'s in Computer Science',
      'Bachelor\'s in Information Technology',
      'Bachelor\'s in Software Engineering',
      'Bachelor\'s in Data Science',
      'Bachelor\'s in Engineering',
      'Bachelor\'s in Business Administration',
      'Bachelor\'s in Commerce (B.Com)',
      'Bachelor\'s in Marketing',
      'Bachelor\'s in Finance',
      'Master\'s in Computer Science',
      'Master\'s in Business Administration (MBA)',
      'Master\'s in Data Science',
      'Any Engineering Degree',
      'Any Graduate Degree',
      'Any Post Graduate Degree',
      'High School Diploma',
      '12th Pass',
      '10th Pass',
      'Graduate (Any Stream)',
      'Post Graduate (Any Stream)',
      'other',
    ]
  };
};

export default {
  requestNotificationPermission,
  saveFCMToken,
  subscribeToTopics,
  unsubscribeFromTopics,
  getUserSubscribedTopics,
  setupMessageListener,
  sendTopicNotification,
  sendPersonalNotification,
  generateTopicsFromJob,
  getAvailableTopics
};
